<template>
  <div class="author-page page-wrap__content-left-margin">
    <a-breadcrumbs :settings="breadcrumbsSettings" capitalize-words />
    <h1 v-if="isInitialPagePaginated" class="author-page__name">
      {{ fullName }}
    </h1>
    <author-details
      v-else
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.AUTHOR_DETAILS
        )
      "
      v-bind="author"
      @mounted="
        $_lazyHydratedComponentState_onHydratedComponentMounted(
          $options.consts.HYDRATED_SECTION_NAME.AUTHOR_DETAILS
        )
      "
    />
    <div class="author-page__articles-container">
      <author-articles-sort @input="onChangeSorting" />
      <a-article-list :articles="articles">
        <template v-slot="{ article }">
          <a-lazy-hydrate
            when-visible
            :key="
              $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
                article.articleId
              )
            "
          >
            <a-article-slot
              v-bind="getArticleSlotProps(article)"
              class="author-page__article"
              @mounted="
                $_lazyHydratedComponentState_onHydratedComponentMounted(
                  article.articleId
                )
              "
            />
          </a-lazy-hydrate>
        </template>
        <template v-slot:banner="{ position }">
          <a-visibility
            v-if="isBannerVisible(position)"
            show
            :on="[$breakpoint.mobile]"
          >
            <a-banner
              :banner-settings="
                $options.consts.BANNER_SETTINGS.AUTHOR_PAGE.MPU_1
              "
              :wrapped-in-card="true"
              class="author-page__banner"
            />
          </a-visibility>
        </template>
      </a-article-list>
      <a-view-more
        :page="pagination.page"
        :pages-count="pagesCount"
        :in-progress="inProgress"
        class="author-page__view-more"
        :view-more-style="$options.consts.VIEW_MORE_STYLE.BUTTON"
        @click="getNextArticleChunk"
      />
      <p v-if="isNoNewsMessageVisible" class="author-page__no-news-found">
        No articles found
      </p>
    </div>
    <a-url-pagination
      :item-selector="ARTICLE_SLOT_SELECTOR"
      :item-count="articles.length"
      :initial-page="initialPage"
      :pagination.sync="pagination"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />

    <a-pagination-links-for-google
      :page="pagination.page"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />

    <a-refresh-listener
      :articles.sync="articles"
      :feed-refresh-in-progress.sync="feedRefreshInProgress"
      :slot-refresh-in-progress.sync="slotRefreshInProgress"
      :fetch-data-fn="fetchLoadedArticles"
      :target-author-slug="$route.params.author"
      :settings="$options.consts.AUTO_REFRESH_SETTINGS.AUTHOR_PAGE"
      @feed-refreshed="$_disqusCommentCount_updateCommentCounts"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import mixins from '@/utils/mixins'
import { SORT_TYPE } from 'shared/ASortByLatestOldest'
import { ROUTE_NAME } from 'enums/routes'
import { throwPageNotFoundError } from '@/plugins/error'
import AUrlPagination from 'shared/AUrlPagination'
import { ARTICLE_SLOT_PROPS } from 'shared/AArticleSlot/enums'
import { SPINNER_ALIGNMENT } from 'shared/AInProgress'
import ARefreshListener from 'shared/ARefreshListener'
import { createPrefilledArray, getPageNumberFromRoute } from '@/plugins/helper'
import { SOURCE } from 'enums/source'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import ABanner from 'shared/ABanner'
import { PAGE_SCHEME_TYPE } from 'enums/pageSchemes'
import { NEWS_ITEM_DATA_ATTRIBUTE } from 'enums/data-attributes'
import {
  METHOD_NAME_TO_RUN_ON_LOGIN,
  METHOD_NAME_TO_RUN_ON_LOGOUT
} from '@/utils/mixins/run-method-on-login-logout'
import { DISQUS_SOURCE } from 'shared/AActionIcon/_shared/CommentCount/enums'
import APaginationLinksForGoogle from 'shared/APaginationLinksForGoogle'
import {
  generatePaginatedCanonicalUrl,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import { AUTO_REFRESH_SETTINGS } from 'enums/article-auto-refresh'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import { METHOD_NAME, PENDING_ACTION_EVENT } from 'enums/pending-actions'
import { applyFallback } from '@/utils/helpers'
import { generateAuthorUrl } from '~/utils/helpers/url-generators'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import AViewMore from 'shared/AViewMore/index.vue'
import { VIEW_MORE_STYLE } from 'shared/AViewMore/enums'
import { pick } from 'ramda'

const DEFAULT_SORTING = SORT_TYPE.LATEST

const DEFAULT_TAKE = 5
const BANNER_POSITION = 3

const ARTICLE_SLOT_SELECTOR = '.author-page__article'

const AUTHOR_MODEL = {
  PhotoUrl: null,
  FirstName: '',
  LastName: '',
  Job: '',
  Company: '',
  Bio: '',
  FacebookUrl: '',
  TwitterUrl: '',
  LinkedInUrl: '',
  TelegramUrl: '',
  Email: '',
  IsFollowed: false
}

function getAuthorSlug(routeParams) {
  return routeParams.author.toLowerCase()
}

function generateDefaultAuthorContentDataModel() {
  return {
    author: {},
    articles: [],
    pagesCount: 0
  }
}

async function getAuthorPageData({ store, route }) {
  const { dispatch } = store

  const initialPage = getPageNumberFromRoute(route)
  const slug = getAuthorSlug(route.params)
  const authorParams = { slug, source: SOURCE.FL }
  const pagination = {
    take: DEFAULT_TAKE,
    page: initialPage
  }
  const authorArticlesParams = {
    slug,
    publishedOn: DEFAULT_SORTING,
    ...pagination
  }

  const requests = [
    dispatch('author/requestAuthor', authorParams),
    dispatch('author/requestAuthorArticles', authorArticlesParams)
  ]

  const [
    author,
    { Articles: articles, PagesCount: pagesCount }
  ] = await Promise.all(requests)

  return {
    author: applyFallback(author, AUTHOR_MODEL),
    articles,
    pagesCount,
    initialPage,
    pagination
  }
}

const HYDRATED_SECTION_NAME = {
  AUTHOR_DETAILS: 'AUTHOR_DETAILS'
}

export default {
  name: 'AuthorPage',
  scrollToTop: true,
  mixins: [
    mixins.refreshDataHandler,
    mixins.articleDataHandler,
    mixins.runMethodOnLoginLogout,
    mixins.setPageLoaded,
    mixins.disqusCommentCountMixinFactory({ source: DISQUS_SOURCE.IL }),
    mixins.pendingAuthActionsListenersFactory({
      [PENDING_ACTION_EVENT.REFRESH_FOLLOW_AUTHOR]:
        METHOD_NAME.UPDATE_IS_FOLLOWED_AUTHOR_VALUE
    }),
    mixins.lazyHydratedComponentState,
    mixins.delayedContentRefresh
  ],
  validate({ params: { author } }) {
    return !!author
  },
  components: {
    AViewMore,
    AArticleSlot: hydrateWhenVisible(
      () => import('@/components/_shared/AArticleSlot'),
      {
        props: ARTICLE_SLOT_PROPS
      }
    ),
    ABanner,
    AuthorDetails: () => import('@/components/_sections/author/AuthorDetails'),
    AuthorArticlesSort: hydrateWhenVisible(() =>
      import('@/components/_sections/author/AuthorArticlesSort')
    ),
    AUrlPagination,
    AArticleList: hydrateWhenVisible(() => import('shared/AArticleList'), {
      props: ['articles']
    }),
    // AInProgress
    ARefreshListener,
    APaginationLinksForGoogle
  },
  consts: {
    NEWS_ITEM_DATA_ATTRIBUTE,
    AUTO_REFRESH_SETTINGS,
    BANNER_SETTINGS,
    VIEW_MORE_STYLE,
    HYDRATED_SECTION_NAME
  },
  head() {
    const { Job: jobTitle, FirstName, LastName } = this.author
    const name = `${FirstName} ${LastName}`
    const url = this.$helper.getFullPath(this)

    return this.$generateMetaTags({
      titleParam: name,
      descriptionParam: name,
      schemes: [
        {
          type: PAGE_SCHEME_TYPE.PERSON,
          data: {
            name,
            jobTitle,
            url
          }
        }
      ],
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route

    try {
      const data = await getAuthorPageData({
        store: ctx.store,
        route: ctx.route
      })

      if (data.pagesCount && data.initialPage > data.pagesCount - 1) {
        return throwPageNotFoundError(ctx, 'AuthorPage')
      }

      const canonicalUrlWithoutPagination = generateAuthorUrl(
        {
          slug: data.author.Slug
        },
        ctx
      )
      const canonicalUrl = generatePaginatedCanonicalUrl({
        canonicalUrlWithoutPagination,
        path: currentPath
      })

      if (
        checkIfNonCanonicalPath(ctx, {
          currentPath,
          canonicalUrl
        })
      ) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      return {
        ...data,
        canonicalUrl,
        canonicalUrlWithoutPagination,
        publishedOn: DEFAULT_SORTING,
        feedRefreshInProgress: false,
        slotRefreshInProgress: createPrefilledArray(
          data.articles.length,
          false
        ),
        isInitialPagePaginated: !!data.initialPage
      }
    } catch (err) {
      ctx.app.$errorHandler(err, ctx, {
        showErrorPage: true,
        showMessage: false
      })
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    const initialPage = getPageNumberFromRoute(this.$route)

    return {
      ...generateDefaultAuthorContentDataModel(),
      canonicalUrl: this.$route.path,
      canonicalUrlWithoutPagination: this.$route.path,
      ARTICLE_SLOT_SELECTOR,
      SPINNER_ALIGNMENT,
      publishedOn: DEFAULT_SORTING,
      pagination: {
        take: DEFAULT_TAKE,
        page: initialPage
      },
      initialPage,
      inProgress: false,
      feedRefreshInProgress: false,
      slotRefreshInProgress: [],
      isInitialPagePaginated: false
    }
  },
  computed: {
    breadcrumbsSettings() {
      return {
        [ROUTE_NAME.AUTHOR_AUTHOR]: {
          nameHandler: () => this.fullName
        }
      }
    },
    fullName() {
      return `${this.author.FirstName} ${this.author.LastName}`
    },
    isNoNewsMessageVisible() {
      return !this.inProgress && this.articles && this.articles.length === 0
    }
  },
  methods: {
    ...mapActions({
      requestAuthorArticles: 'author/requestAuthorArticles'
    }),
    getArticleSlotProps(article) {
      return {
        ...article,
        [this.$options.consts.NEWS_ITEM_DATA_ATTRIBUTE]: true
      }
    },
    getNextArticleChunk() {
      this.pagination.page++
      this.getArticles()
    },
    async getArticles() {
      try {
        this.inProgress = true
        const {
          Articles: articles,
          PagesCount: pagesCount
        } = await this.requestAuthorArticles({
          slug: getAuthorSlug(this.$route.params),
          publishedOn: this.publishedOn,
          ...this.pagination
        })
        this.articles = [...this.articles, ...articles]
        this.slotRefreshInProgress = createPrefilledArray(
          this.articles.length,
          false
        )
        this.pagesCount = pagesCount
      } catch (err) {
        this.$errorHandler(err, this, {
          showErrorPage: true,
          showMessage: false
        })
      } finally {
        this.inProgress = false
      }
    },
    async onChangeSorting(sorting) {
      this.publishedOn = sorting

      const currentPathWithoutPagination = this.$route.fullPath.replace(
        /page\/\d+\/+/,
        ''
      )
      window.history.replaceState(null, '', currentPathWithoutPagination)

      this.rerenderArticles()
    },
    rerenderArticles() {
      this.clearData()
      this.getArticles()
    },
    clearData() {
      this.articles = []
      this.initialPage = 0
      this.pagesCount = 0
      this.pagination.page = 0
    },
    isBannerVisible(index) {
      const position = index + 1
      return position === BANNER_POSITION
    },
    async fetchLoadedArticles() {
      const payload = {
        slug: getAuthorSlug(this.$route.params),
        publishedOn: this.publishedOn
      }
      const articleChunks = await this.$_articleDataHandler_fetchLoadedArticles(
        {
          action: this.requestAuthorArticles,
          initialPage: this.initialPage,
          take: this.pagination.take,
          length: this.articles.length,
          payload
        }
      )
      return articleChunks.reduce(
        (acc, { Articles }) => [...acc, ...Articles],
        []
      )
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      this.author.IsFollowed = false
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      this.author.IsFollowed = await this.getIsFollowedAuthor()
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_AUTHOR_VALUE]({
      id,
      value,
      newFollowerCount
    }) {
      if (this.author.Id === id) {
        this.author.IsFollowed = value
        this.author.FollowerCount = newFollowerCount
      }
    },
    getIsFollowedAuthor() {
      const slug = this.$route.params.author.toLowerCase()
      return this.$_refreshDataHandler_getIsFollowedAuthorValue({
        slug,
        source: SOURCE.FL
      })
    },
    refreshAuthorPageContent() {
      const dataKeysForDelayedContentRefresh = Object.keys(
        generateDefaultAuthorContentDataModel()
      )

      this.$_delayedContentRefresh_run({
        getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
        requestNewDataFn: async () => {
          const newData = await getAuthorPageData({
            route: this.$route,
            store: this.$store
          })

          return pick(dataKeysForDelayedContentRefresh, newData)
        },
        dataComparisonKeysToOmitDeep: ['lastModified', 'ModifiedOn'],
        dataChangedCallbackFn: newData => {
          Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))
          const componentIdentifiers = this.articles.map(
            article => article.articleId
          )
          this.$_lazyHydratedComponentState_rerenderNonHydratedComponents(
            componentIdentifiers
          )
          this.$_lazyHydratedComponentState_rerenderNonHydratedComponents(
            Object.values(HYDRATED_SECTION_NAME)
          )
        }
      })
    }
  },
  mounted() {
    this.refreshAuthorPageContent()
  }
}
</script>

<style lang="scss" scoped>
.author-page {
  .author-page__articles-container,
  .author-page__name {
    padding-left: 380px;

    @media (max-width: 1652px) {
      padding-left: 0;
    }

    @include desktop-md {
      padding-left: 360px;
    }

    @include desktop-sm {
      padding-left: 0;
    }
  }

  .author-page__article,
  .author-page__name {
    margin-bottom: 20px;
  }

  .author-page__banner {
    margin-top: 24px;
    margin-bottom: 24px;
  }

  .author-page__view-more {
    $margin: (
      $above-desktop-xl: 20px 0 60px,
      $tablet: 20px 0 40px,
      $mobile: 20px 0 24px
    );

    @include setStyleByBreakpoint('margin', $margin);
  }

  .author-page__no-news-found {
    text-align: center;
  }
}
</style>
